import { Box, Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { ProviderLogo } from '../components/shared/ProviderLogo';
import type { ProviderBrand } from '../components/shared/providerBrand';
import { AuthDivider } from './AuthDivider';

export interface SsoProvider {
  id: string;
  name: string;
  type: string;
  /** Worked out by the server from the issuer; older servers send none. */
  brand?: ProviderBrand;
}

/** Per-browser only: which provider to offer first on the next visit. */
const LAST_PROVIDER_KEY = 'teamlane.lastSsoProviderId';

export function rememberSsoProvider(providerId: string): void {
  try {
    localStorage.setItem(LAST_PROVIDER_KEY, providerId);
  } catch {
    // Blocked storage only costs the "last used first" ordering.
  }
}

function lastSsoProvider(): string | null {
  try {
    return localStorage.getItem(LAST_PROVIDER_KEY);
  } catch {
    return null;
  }
}

/**
 * The providers in the order to offer them: the one this browser last signed
 * in with first, then the admin's order (the server sends them in it, the
 * first being the workspace default).
 */
export function orderForThisDevice(providers: SsoProvider[]): { ordered: SsoProvider[]; lastUsed: boolean } {
  const last = lastSsoProvider();
  const index = providers.findIndex((p) => p.id === last);
  if (index <= 0) return { ordered: providers, lastUsed: index === 0 };
  return { ordered: [providers[index], ...providers.filter((_, i) => i !== index)], lastUsed: true };
}

interface SsoProviderButtonsProps {
  providers: SsoProvider[];
  loading: boolean;
  onSelect: (providerId: string) => void;
  /**
   * `primary`: SSO is the way in, so the first provider gets the filled button
   * and the rest sit below it. `secondary`: a password form leads the page, so
   * every provider is an outlined button under it.
   */
  emphasis: 'primary' | 'secondary';
}

export function SsoProviderButtons({ providers, loading, onSelect, emphasis }: SsoProviderButtonsProps) {
  const theme = useTheme();
  const { ordered, lastUsed } = orderForThisDevice(providers);
  if (ordered.length === 0) return null;

  const spinner = <CircularProgress size={16} color="inherit" />;
  const outlinedSx = {
    fontWeight: 500,
    borderColor: theme.palette.ink.line,
    color: 'text.primary',
    '&:hover': { borderColor: 'text.disabled' },
  } as const;
  const logo = (p: SsoProvider, size = 16) => <ProviderLogo brand={p.brand ?? 'generic'} size={size} />;

  if (emphasis === 'secondary') {
    return (
      <Stack spacing={1}>
        {ordered.map((p) => (
          <Button
            key={p.id}
            variant="outlined"
            fullWidth
            size="large"
            disabled={loading}
            startIcon={loading ? spinner : logo(p)}
            onClick={() => onSelect(p.id)}
            sx={{ ...outlinedSx, py: 1.375, fontSize: '0.84375rem' }}
          >
            Sign in with {p.name}
          </Button>
        ))}
      </Stack>
    );
  }

  const [primary, ...others] = ordered;
  return (
    <Stack spacing={2.75}>
      <Stack spacing={1}>
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={loading}
          onClick={() => onSelect(primary.id)}
          startIcon={
            loading ? (
              spinner
            ) : (
              // Brand guidelines want the full-colour mark on white, not on the accent.
              <Box
                component="span"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: 'common.white',
                  color: 'common.black',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {logo(primary, 18)}
              </Box>
            )
          }
          sx={{ py: 1.25, fontSize: '0.90625rem', fontWeight: 500 }}
        >
          Continue with {primary.name}
        </Button>
        {lastUsed && others.length > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            You last signed in with {primary.name} on this device.
          </Typography>
        )}
      </Stack>
      {others.length > 0 && (
        <>
          <AuthDivider label="Other ways to sign in" />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
            {others.map((p) => (
              <Button
                key={p.id}
                variant="outlined"
                fullWidth
                disabled={loading}
                startIcon={logo(p)}
                onClick={() => onSelect(p.id)}
                sx={{ ...outlinedSx, py: 1.125, fontSize: '0.84375rem' }}
              >
                {p.name}
              </Button>
            ))}
          </Box>
        </>
      )}
    </Stack>
  );
}
